import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, ShieldCheck, Award, Calendar, FileText, ExternalLink, Copy } from 'lucide-react';

// Production-safe API host: same-origin in deployed builds, env var in preview.
const _ENV_BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const _ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const _isPreviewEnv = /preview\.emergentagent\.com$/.test(
  (typeof window !== 'undefined' && window.location.hostname) || ''
);
const API_HOST = _isPreviewEnv ? _ENV_BACKEND : (_ORIGIN || _ENV_BACKEND);

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (_e) {
    return iso;
  }
};

const VerifyCertificatePage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [lookupValue, setLookupValue] = useState('');

  useEffect(() => {
    let active = true;
    const run = async () => {
      try {
        const r = await fetch(`${API_HOST}/api/certificates/verify/${encodeURIComponent(code)}`);
        const data = await r.json();
        if (active) setResult(data);
      } catch (_e) {
        if (active) setResult({ valid: false, message: 'Verification service is unreachable. Please try again.' });
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => { active = false; };
  }, [code]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (_e) { /* ignore */ }
  };

  return (
    <div data-testid="verify-certificate-page" style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg,#0b2545 0%,#13315c 60%,#0b2545 100%)',
      padding: '40px 20px',
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Helvetica, Arial, sans-serif',
    }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        {/* Top brand strip */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20, color: '#f7d774' }}>
          <img
            src={`${process.env.PUBLIC_URL || ''}/medcare_seal.png`}
            alt="MedCare Institutional Seal"
            width="56"
            height="56"
            style={{ objectFit: 'contain', borderRadius: '50%', background: '#ffffff' }}
            data-testid="verify-seal"
          />
          <div>
            <div style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 2, opacity: 0.85 }}>
              MedCare Certificate Verification
            </div>
            <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>
              U.S. Department of Labor · Registered Apprenticeship
            </div>
          </div>
        </div>

        {/* Verification card */}
        <div style={{
          background: '#ffffff',
          borderRadius: 16,
          boxShadow: '0 20px 60px -20px rgba(0,0,0,0.4)',
          overflow: 'hidden',
          border: '1px solid rgba(247,215,116,0.4)',
        }}>
          {loading && (
            <div data-testid="verify-loading" style={{ padding: '60px 40px', textAlign: 'center', color: '#64748b' }}>
              <div style={{ fontSize: '0.9rem' }}>Verifying credential…</div>
            </div>
          )}

          {!loading && result && result.valid && (
            <>
              <div style={{
                background: 'linear-gradient(135deg,#15803d,#22c55e)',
                color: '#ffffff',
                padding: '22px 32px',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
              }} data-testid="verify-success-header">
                <CheckCircle size={32} />
                <div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, letterSpacing: 0.3 }}>
                    Verified · Authentic Credential
                  </div>
                  <div style={{ fontSize: '0.85rem', opacity: 0.95 }}>
                    This certificate is on file with MedCare Nursing Institute &amp; Training Academy.
                  </div>
                </div>
              </div>

              <div style={{ padding: '28px 32px 8px' }}>
                <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 1.4, color: '#64748b', fontWeight: 700 }}>Awarded To</div>
                <h1 data-testid="verify-student-name" style={{
                  margin: '4px 0 16px',
                  fontSize: '2rem',
                  color: '#0b2545',
                  fontFamily: 'Georgia, "Times New Roman", serif',
                }}>
                  {result.student_name || '—'}
                </h1>

                <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 1.4, color: '#64748b', fontWeight: 700 }}>Program</div>
                <div data-testid="verify-program-title" style={{ fontSize: '1.15rem', color: '#0b2545', fontWeight: 700, marginBottom: 18, lineHeight: 1.35 }}>
                  {result.program_title}
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: 14,
                  marginBottom: 18,
                }}>
                  <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px', borderLeft: '3px solid #0d9488' }}>
                    <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 1.2, color: '#64748b', fontWeight: 700 }}>Completed</div>
                    <div data-testid="verify-completed-at" style={{ fontSize: '1rem', color: '#0b2545', fontWeight: 700, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Calendar size={14} /> {formatDate(result.completed_at)}
                    </div>
                  </div>
                  <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px', borderLeft: '3px solid #b45309' }}>
                    <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 1.2, color: '#64748b', fontWeight: 700 }}>Verification Code</div>
                    <div data-testid="verify-code" style={{ fontSize: '0.95rem', color: '#0b2545', fontWeight: 700, marginTop: 4, fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', wordBreak: 'break-all' }}>
                      {result.verification_code}
                    </div>
                    {result.is_legacy_alias && (
                      <div
                        data-testid="verify-legacy-alias-notice"
                        style={{ fontSize: '0.72rem', color: '#7c2d12', marginTop: 6, fontStyle: 'italic', lineHeight: 1.4 }}
                      >
                        You searched <code style={{ fontFamily: 'ui-monospace, monospace', background: '#fef3c7', padding: '1px 4px', borderRadius: 3 }}>{result.looked_up_code}</code> — that earlier code remains valid. The certificate was re-issued under the new CMA prefix on {new Date(result.certificate_generated_at).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}.
                      </div>
                    )}
                  </div>
                  <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px', borderLeft: '3px solid #9333ea' }}>
                    <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 1.2, color: '#64748b', fontWeight: 700 }}>Apprentice ID</div>
                    <div data-testid="verify-apprentice-id" style={{ fontSize: '1rem', color: '#0b2545', fontWeight: 700, marginTop: 4 }}>
                      {result.apprentice_id || 'Not Assigned'}
                    </div>
                  </div>
                  {result.conferral_number && (
                    <div style={{ background: '#fdf4ff', borderRadius: 10, padding: '12px 14px', borderLeft: '3px solid #5c1a1b', gridColumn: '1 / -1' }}>
                      <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 1.2, color: '#5c1a1b', fontWeight: 700 }}>Doctoral Conferral No.</div>
                      <div data-testid="verify-conferral-number" style={{ fontSize: '1rem', color: '#5c1a1b', fontWeight: 800, marginTop: 4, fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace' }}>
                        {result.conferral_number}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#7c2d12', marginTop: 4, fontStyle: 'italic' }}>
                        Vital Health Association · Doctoral Registry
                      </div>
                    </div>
                  )}
                </div>

                <div style={{
                  borderTop: '1px solid #e5e7eb',
                  marginTop: 6,
                  paddingTop: 16,
                  fontSize: '0.85rem',
                  color: '#475569',
                  lineHeight: 1.6,
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                    <ShieldCheck size={16} color="#15803d" style={{ marginTop: 2 }} />
                    <div>
                      <strong>Issued by:</strong> {result.issuer}<br />
                      <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{result.issuer_division}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <Award size={16} color="#b45309" style={{ marginTop: 2 }} />
                    <div>{result.registration}</div>
                  </div>
                </div>
              </div>

              <div style={{ padding: '16px 32px 28px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={copyLink}
                  data-testid="verify-copy-link-btn"
                  style={{
                    background: '#0b2545',
                    color: '#ffffff',
                    border: 'none',
                    padding: '10px 16px',
                    borderRadius: 8,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Copy size={14} /> {copied ? 'Copied!' : 'Copy share link'}
                </button>
                <Link
                  to="/"
                  data-testid="verify-home-link"
                  style={{
                    background: '#f1f5f9',
                    color: '#0b2545',
                    border: '1px solid #cbd5e1',
                    padding: '10px 16px',
                    borderRadius: 8,
                    fontWeight: 700,
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <ExternalLink size={14} /> medcaretrainingacademy.com
                </Link>
              </div>
            </>
          )}

          {!loading && result && !result.valid && (
            <>
              <div style={{
                background: 'linear-gradient(135deg,#991b1b,#dc2626)',
                color: '#ffffff',
                padding: '22px 32px',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
              }} data-testid="verify-invalid-header">
                <XCircle size={32} />
                <div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800 }}>No Match Found</div>
                  <div style={{ fontSize: '0.85rem', opacity: 0.95 }}>
                    We could not locate a certificate matching this verification code.
                  </div>
                </div>
              </div>
              <div style={{ padding: '24px 32px' }}>
                <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 12 }}>
                  Submitted code:
                </div>
                <div data-testid="verify-invalid-code" style={{
                  fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 6,
                  padding: '8px 12px',
                  fontWeight: 700,
                  color: '#0b2545',
                  wordBreak: 'break-all',
                }}>
                  {code}
                </div>
                <p style={{ marginTop: 18, fontSize: '0.88rem', color: '#475569', lineHeight: 1.55 }}>
                  Double-check the code printed on the certificate (format: <code>MC-XXX-XXXXX-YYYYMMDD</code>).
                  If you believe this is in error, contact <a href="mailto:medcare.nursing@yahoo.com" style={{ color: '#0d9488' }}>medcare.nursing@yahoo.com</a> or call <strong>901.625.1201</strong>.
                </p>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const v = lookupValue.trim();
                    if (v) navigate(`/verify/${encodeURIComponent(v)}`);
                  }}
                  data-testid="verify-lookup-form"
                  style={{ marginTop: 18, display: 'flex', gap: 8, flexWrap: 'wrap' }}
                >
                  <input
                    type="text"
                    value={lookupValue}
                    onChange={(e) => setLookupValue(e.target.value)}
                    placeholder="Enter another verification code…"
                    data-testid="verify-lookup-input"
                    style={{
                      flex: '1 1 280px',
                      padding: '10px 12px',
                      border: '1px solid #cbd5e1',
                      borderRadius: 8,
                      fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                      fontSize: '0.92rem',
                    }}
                  />
                  <button
                    type="submit"
                    data-testid="verify-lookup-submit"
                    style={{
                      background: '#0d9488', color: '#ffffff', border: 'none',
                      padding: '10px 18px', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    Verify
                  </button>
                </form>
                <div style={{ marginTop: 16 }}>
                  <Link
                    to="/"
                    data-testid="verify-back-home-link"
                    style={{
                      background: '#0b2545', color: '#ffffff',
                      padding: '10px 16px', borderRadius: 8, fontWeight: 700,
                      textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    <FileText size={14} /> Back to MedCare home
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ marginTop: 22, color: '#cbd5e1', fontSize: '0.8rem', textAlign: 'center', lineHeight: 1.6 }}>
          © 2026 MedCare Nursing Institute &amp; Training Academy. All rights reserved.<br />
          A program of the MedCare National Board of Nursing &amp; Vital Health Sciences Association.
        </div>
      </div>
    </div>
  );
};

export default VerifyCertificatePage;
